<script setup>
import { ref, computed } from 'vue'
import StatusPill from '@/components/shell/StatusPill.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'
import { STAGES, STAGE } from '@/data/shared/orderModel.js'

const search = ref(''); const statusFilter = ref('All')
const drawerOrderNo = ref(null)
const commentText = ref(''); const cancelReason = ref('')

const orders = computed(() => store.salesSupervisorV2.orders.value)
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return orders.value.filter(o => {
    const matchesQ = !q || o.orderNo.toLowerCase().includes(q) || o.pharmacyName.toLowerCase().includes(q) || o.execName.toLowerCase().includes(q)
    const matchesStatus = statusFilter.value === 'All' || o.currentStage === statusFilter.value
    return matchesQ && matchesStatus
  }).sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))
})
const statusOptions = computed(() => ['All', ...new Set(orders.value.map(o => o.currentStage))])
const drawerOrder = computed(() => drawerOrderNo.value ? orders.value.find(o => o.orderNo === drawerOrderNo.value) || null : null)
const stageIdx = computed(() => drawerOrder.value ? STAGES.indexOf(drawerOrder.value.currentStage) : -1)
const isLateral = computed(() => drawerOrder.value && !STAGES.includes(drawerOrder.value.currentStage))
const canAct = computed(() => drawerOrder.value && drawerOrder.value.currentStage !== 'Delivered' && drawerOrder.value.currentStage !== 'Cancelled')
// Approve is only meaningful at the supervisor's own gate — an order still
// awaiting the pharmacy's OTP isn't the supervisor's to release yet.
const canApprove = computed(() => drawerOrder.value && drawerOrder.value.currentStage === STAGE.SUPERVISOR_APPROVAL)

function openDrawer(o) { drawerOrderNo.value = o.orderNo; commentText.value = ''; cancelReason.value = '' }
function approve() { if (drawerOrderNo.value) store.salesSupervisorV2.approveOrder(drawerOrderNo.value) }
function cancel() { if (drawerOrderNo.value) { store.salesSupervisorV2.cancelOrder(drawerOrderNo.value, cancelReason.value.trim()); cancelReason.value = '' } }
function comment() { if (drawerOrderNo.value && commentText.value.trim()) { store.salesSupervisorV2.addComment(drawerOrderNo.value, commentText.value.trim()); commentText.value = '' } }
</script>

<template>
  <div>
    <div class="card module-header"><h2>{{ orders.length }} team orders</h2></div>
    <div class="card">
      <div class="toolbar">
        <input class="search-input" v-model="search" placeholder="Search order no., pharmacy or exec…" />
        <select class="filter-select" v-model="statusFilter"><option v-for="s in statusOptions" :key="s">{{ s }}</option></select>
        <span class="result-count">{{ filtered.length }} of {{ orders.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Order No.</th><th>Exec</th><th>Pharmacy</th><th>Value</th><th>Status</th></tr></thead>
          <tbody>
            <tr v-for="o in filtered" :key="o.orderNo" class="clickable" @click="openDrawer(o)">
              <td class="cell-strong">{{ o.orderNo }}</td><td>{{ o.execName }}</td><td>{{ o.pharmacyName }}</td>
              <td class="cell-muted">${{ o.orderValue.toLocaleString() }}</td>
              <td><StatusPill :status="o.currentStage" :label="o.currentStage" /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <DetailDrawer :open="!!drawerOrder" :title="drawerOrder?.orderNo || ''" @close="drawerOrderNo=null">
      <template v-if="drawerOrder">
        <div class="oos-meta-grid" style="margin-bottom:18px;">
          <div><div class="label">Sales Exec</div>{{ drawerOrder.execName }}</div>
          <div><div class="label">Pharmacy</div>{{ drawerOrder.pharmacyName }}</div>
          <div><div class="label">Value</div>${{ drawerOrder.orderValue.toLocaleString() }}</div>
        </div>
        <table class="data-table" style="margin-bottom:18px;">
          <thead><tr><th>Product</th><th>Ordered</th><th>Free</th><th>Total Delivered</th><th>Line Value</th></tr></thead>
          <tbody>
            <tr v-for="li in drawerOrder.lineItems" :key="li.productSku">
              <td class="cell-strong">{{ li.productName }} <span v-if="li.schemeApplied" class="tier-badge Gold">10+1</span></td>
              <td class="cell-muted">{{ li.quantity }}</td>
              <td class="cell-muted">{{ li.freeQuantity || 0 }}</td>
              <td class="cell-strong">{{ li.totalQuantity || li.quantity }}</td>
              <td class="cell-muted">${{ li.lineValue.toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="!isLateral" class="stepper" style="margin-bottom:18px;">
          <div v-for="(s,i) in STAGES" :key="s" class="stepper-step">
            <div class="stepper-marker">
              <div class="stepper-dot" :class="{ done: i<stageIdx, current: i===stageIdx }"></div>
              <div v-if="i<STAGES.length-1" class="stepper-line" :class="{ done: i<stageIdx }"></div>
            </div>
            <div class="stepper-label" :class="i===stageIdx?'current':(i<stageIdx?'':'pending')">{{ s }}</div>
          </div>
        </div>
        <div v-if="drawerOrder.currentStage==='Cancelled'" class="oos-remarks" style="margin-bottom:16px;">{{ drawerOrder.cancelReason || '—' }}</div>
        <div v-if="drawerOrder.comments && drawerOrder.comments.length" style="margin-bottom:16px;">
          <div v-for="(c,i) in drawerOrder.comments" :key="i" class="oos-remarks" style="margin-bottom:8px;">
            <strong style="display:block;font-size:11px;color:var(--color-text-muted);">{{ c.from }} — {{ new Date(c.timestamp).toLocaleString() }}</strong>{{ c.text }}
          </div>
        </div>

        <div v-if="canAct" style="border-top:1px solid var(--color-border);padding-top:16px;">
          <button v-if="canApprove" class="btn-primary" style="margin-right:8px;" @click="approve">Approve</button>
          <p v-else-if="drawerOrder.currentStage === STAGE.OTP_CONFIRMATION" class="cell-muted" style="font-size:12px;margin:0 0 8px;">Awaiting pharmacy OTP confirmation — not yet ready for approval.</p>
          <p v-else class="cell-muted" style="font-size:12px;margin:0 0 8px;">Already approved — currently at {{ drawerOrder.currentStage }}.</p>
          <div style="display:flex;gap:8px;margin:10px 0;">
            <input class="form-input" v-model="cancelReason" placeholder="Cancel reason" />
            <button class="filter-select" style="cursor:pointer;color:var(--status-red);" @click="cancel">Cancel</button>
          </div>
          <div style="display:flex;gap:8px;">
            <input class="form-input" v-model="commentText" placeholder="Comment back to Sales Exec…" />
            <button class="filter-select" style="cursor:pointer;" @click="comment">Comment</button>
          </div>
        </div>
      </template>
    </DetailDrawer>
  </div>
</template>
